import React, { useState } from 'react';
import Modal from 'react-modal';
import JobPostForm from './job-post-form';
import jobPostData from './job-post-data';
import styled from 'styled-components';
import { mixins, theme } from '@styles';
const { colors } = theme;

const customModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: '0px',
    width: '40vw',
    minWidth: '480px',
  },
  overlay: { zIndex: 999 },
};

const nameValidation = (fieldName, fieldValue) => {
  if (fieldValue.trim() === '') {
    return `${fieldName} is required`;
  }
  if (/[^a-zA-Z -]/.test(fieldValue)) {
    return 'Invalid characters';
  }
  if (fieldValue.trim().length < 3) {
    return `${fieldName} needs to be at least three characters`;
  }
  return null;
};

const emailValidation = email => {
  if (/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email)) {
    return null;
  }
  if (email.trim() === '') {
    return 'Email is required';
  }
  return 'Please enter a valid email';
};

const mobilePhoneValidation = number => {
  if (!number) {
    return 'Mobile Phone is required';
  }

  const phoneno = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
  if (number.match(phoneno)) {
    return null;
  }

  return 'Please enter a valid mobile phone';
};

const resumeValidation = file => {
  if (!file) {
    return 'Resume is required';
  }
  return `Please enter a valid Resume's file`;
};

const validate = {
  firstName: name => nameValidation('First Name', name),
  lastName: name => nameValidation('Last Name', name),
  email: emailValidation,
  mobilePhone: mobilePhoneValidation,
  resume: resumeValidation,
};

const JobPostModal = ({ isOpen, handleCloseModal, data, handleNotification }) => {
  const {
    jobOrderId,
    isPublic,
    title,
    publicDescription,
    publishedCategory,
    address,
    employmentType,
    dateLastPublished,
  } = data;

  const [values, setValues] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobilePhone: '',
  });

  const [files, setFiles] = useState();

  const [errors, setErrors] = useState({});

  const [touched, setTouched] = useState({});

  const handleChange = event => {
    const { value, name } = event.target;
    setValues({
      ...values,
      [name]: value,
    });
    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleBlur = evt => {
    const { name, value } = evt.target;
    const { [name]: removedError, ...rest } = errors;
    const error = validate[name](value);
    setErrors({
      ...rest,
      ...(error && { [name]: touched[name] && error }),
    });
  };

  const handleSubmit = event => {
    event.preventDefault();
    const formValidation = Object.keys(values).reduce(
      (acc, key) => {
        const newError = validate[key](values[key]);
        const newTouched = { [key]: true };
        return {
          errors: {
            ...acc.errors,
            ...(newError && { [key]: newError }),
          },
          touched: {
            ...acc.touched,
            ...newTouched,
          },
        };
      },
      {
        errors: { ...errors },
        touched: { ...touched },
      },
    );
    setErrors(formValidation.errors);
    setTouched(formValidation.touched);

    if (
      !Object.values(formValidation.errors).length &&
      Object.values(formValidation.touched).length === Object.values(values).length &&
      Object.values(formValidation.touched).every(t => t === true)
    ) {
      if (!files) {
        const { ['resume']: removedError, ...rest } = errors;
        const error = validate['resume']();
        setErrors({
          ...rest,
          ...(error && { ['resume']: error }),
        });
      } else {
        const requestParams = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.mobilePhone,
          format: files[0].name.substring(files[0].name.lastIndexOf('.') + 1),
        };
        let formData = new FormData();
        formData.append('resume', files[0]);
        jobPostData(jobOrderId, requestParams, formData)
          .then(res => {
            handleNotification();
            handleCloseModal();
          })
          .catch(error => {
            console.log(error);
          });
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      style={customModalStyles}
      ariaHideApp={false}
      shouldFocusAfterRender={false}>
      <StyledHead>
        <Title>{title}</Title>
        <Category>{publishedCategory.name}</Category>
        <Address>
          {address.city} &nbsp; {address.state}
        </Address>
      </StyledHead>
      <JobPostForm
        handleBlur={handleBlur}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        errors={errors}
        touched={touched}
        values={values}
        handleCloseModal={handleCloseModal}
        setFiles={setFiles}
        setErrors={setErrors}
      />
    </Modal>
  );
};

export default JobPostModal;

const StyledHead = styled.div`
  background-color: ${colors.lightPurple};
  padding: 36px 36px 24px 36px;
  color: white;
`;

const Title = styled.div`
  ${mixins.subtitle32_medium};
  padding-bottom: 8px;
`;

const Category = styled.div`
  ${mixins.textmd_medium};
`;

const Address = styled.div`
  ${mixins.textmd_medium};
`;
